import axios from 'axios';

const getApiUrl = () => {
  if (process.env.REACT_APP_API_URL) {
    const rawApi = process.env.REACT_APP_API_URL;
    return rawApi.endsWith('/api') ? rawApi : `${rawApi}/api`;
  }
  return 'http://localhost:3003/api';
};

const API_URL = getApiUrl();

console.log('🔗 API URL configured as:', API_URL);
console.log('🌍 Current hostname:', window.location.hostname);
console.log('📦 Environment:', process.env.NODE_ENV);

// Create axios instance with proper configuration
const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000, // 30 second timeout
  withCredentials: false // Add this for CORS
});

// Request interceptor
api.interceptors.request.use(
  (config) => {
    // Add authorization token
    const token = localStorage.getItem('adminToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    // Add CSRF token if available
    const csrf = localStorage.getItem('csrfToken');
    if (csrf) {
      config.headers['X-CSRF-Token'] = csrf;
    }
    
    console.log('📤 API Request:', config.method?.toUpperCase(), config.url);
    return config;
  },
  (error) => {
    console.error('❌ Request error:', error);
    return Promise.reject(error);
  }
);

// Response interceptor
api.interceptors.response.use(
  (response) => {
    console.log('✅ API Response:', response.status, response.config.url);
    return response;
  },
  (error) => {
    console.error('❌ API Error:', {
      status: error.response?.status,
      data: error.response?.data,
      url: error.config?.url,
      message: error.message
    });
    
    // Handle specific error cases
    if (error.response?.status === 401) {
      // Unauthorized - clear tokens
      localStorage.removeItem('adminToken');
      localStorage.removeItem('csrfToken');
      
      // Redirect to login if on admin page
      if (window.location.pathname.includes('/admin')) {
        window.location.href = '/admin';
      }
    }
    
    return Promise.reject(error);
  }
);

/* =====================================================
   EVENTS API - FIXED WITH COMPREHENSIVE ERROR HANDLING
===================================================== */
export const fetchEvents = async () => {
  try {
    console.log('🔍 Fetching events from:', `${API_URL}/events`);
    
    const response = await fetch(`${API_URL}/events`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      mode: 'cors'
    });

    console.log('📊 Events response status:', response.status);
    console.log('📋 Events response headers:', Object.fromEntries(response.headers.entries()));

    // Check if response is ok
    if (!response.ok) {
      const contentType = response.headers.get('content-type');
      let errorMessage = `HTTP error! status: ${response.status}`;
      
      // Try to get error details
      if (contentType && contentType.includes('application/json')) {
        try {
          const errorData = await response.json();
          errorMessage = errorData.message || errorMessage;
          console.error('❌ JSON error response:', errorData);
        } catch (e) {
          console.error('❌ Failed to parse error JSON');
        }
      } else {
        // Server returned HTML or plain text instead of JSON
        const errorText = await response.text();
        console.error('❌ Non-JSON error response (first 500 chars):', errorText.slice(0, 500));
        
        // Check if it's an HTML error page
        if (errorText.includes('<html') || errorText.includes('<!DOCTYPE')) {
          errorMessage = `Server returned HTML error page (status ${response.status}). The API endpoint may not exist or the server may be misconfigured.`;
        } else {
          errorMessage = `Server error (status ${response.status}): ${errorText.slice(0, 100)}`;
        }
      }
      
      throw new Error(errorMessage);
    }

    // Check content type
    const contentType = response.headers.get('content-type');
    console.log('📄 Content-Type:', contentType);
    
    if (!contentType || !contentType.includes('application/json')) {
      const rawText = await response.text();
      console.error('❌ Non-JSON response received (first 500 chars):', rawText.slice(0, 500));
      
      // Check if we got HTML instead of JSON
      if (rawText.includes('<html') || rawText.includes('<!DOCTYPE')) {
        throw new Error(
          `API returned HTML instead of JSON. This usually means:\n` +
          `1. The API endpoint "/events" doesn't exist\n` +
          `2. The backend server is not running\n` +
          `3. The API URL is incorrect\n\n` +
          `Current API URL: ${API_URL}/events\n` +
          `Please verify your backend is running and the endpoint exists.`
        );
      }
      
      throw new Error(`Expected JSON but got ${contentType || 'unknown content type'}. Response: ${rawText.slice(0, 200)}`);
    }

    // Parse JSON safely
    let data;
    try {
      data = await response.json();
      console.log('✅ Events data received:', data);
      console.log('📦 Data structure:', {
        isArray: Array.isArray(data),
        hasData: 'data' in data,
        hasEvents: 'events' in data,
        keys: Object.keys(data)
      });
    } catch (jsonErr) {
      console.error('❌ JSON parsing error:', jsonErr);
      throw new Error('Invalid JSON response from events API. Server may be misconfigured or returning malformed data.');
    }
    
    // Handle different response formats
    if (data.success && data.data) {
      console.log('✅ Using data.success + data.data format');
      return { data: data.data };
    } else if (data.events) {
      console.log('✅ Using data.events format');
      return { data: data.events };
    } else if (Array.isArray(data)) {
      console.log('✅ Using direct array format');
      return { data: data };
    } else {
      console.warn('⚠️ Unexpected data format, returning as-is:', data);
      return { data: [] };
    }
    
  } catch (error) {
    console.error('💥 Error in fetchEvents:', error);
    console.error('💥 Error stack:', error.stack);
    
    // Return empty array with error info instead of throwing
    return { 
      data: [], 
      error: error.message,
      isError: true 
    };
  }
};

export const eventsAPI = {
  getAll: () => fetchEvents(),
  
  getById: async (id) => {
    if (!id || typeof id !== 'string') {
      throw new Error('Invalid event ID format');
    }
    try {
      console.log('🔍 Fetching event by ID:', id);
      const response = await api.get(`/events/${id}`);
      console.log('✅ Event response:', response);
      
      // Handle different response structures
      if (response.data) {
        if (response.data.data) {
          return { data: response.data.data };
        } else if (response.data.event) {
          return { data: response.data.event };
        } else {
          return { data: response.data };
        }
      }
      return response;
    } catch (error) {
      console.error('❌ Error fetching event by ID:', error);
      throw error;
    }
  },
  
  create: async (data) => {
    console.log('Creating event:', data);
    try {
      return await api.post('/events', data);
    } catch (error) {
      console.error('Error creating event:', error);
      throw error;
    }
  },
  
  createWithImage: async (formData) => {
    console.log('Creating event with image');
    try {
      return await api.post('/events', formData, {
        headers: { 
          'Content-Type': 'multipart/form-data'
        }
      });
    } catch (error) {
      console.error('Error creating event with image:', error);
      throw error;
    }
  },
  
  update: async (id, data) => {
    if (!id || typeof id !== 'string') {
      throw new Error('Invalid event ID format');
    }
    console.log('Updating event:', id);
    try {
      return await api.put(`/events/${id}`, data);
    } catch (error) {
      console.error('Error updating event:', error);
      throw error;
    }
  },
  
  delete: async (id) => {
    if (!id || typeof id !== 'string') {
      throw new Error('Invalid event ID format');
    }
    console.log('Deleting event:', id);
    try {
      return await api.delete(`/events/${id}`);
    } catch (error) {
      console.error('Error deleting event:', error);
      throw error;
    }
  },
};

/* =====================================================
   REGISTRATIONS API
===================================================== */
export const registrationsAPI = {
  create: async (data) => {
    console.log('Creating registration:', data);
    try {
      return await api.post('/registrations', data);
    } catch (error) {
      console.error('Error creating registration:', error);
      throw error;
    }
  },
  
  getById: async (id) => {
    if (!id || typeof id !== 'string') {
      throw new Error('Invalid registration ID format');
    }
    try {
      const response = await api.get(`/registrations/${id}`);
      return response.data.data ? { data: response.data.data } : response;
    } catch (error) {
      console.error('Error fetching registration:', error);
      throw error;
    }
  },
  
  getByEvent: async (eventId) => {
    if (!eventId || typeof eventId !== 'string') {
      throw new Error('Invalid event ID format');
    }
    try {
      return await api.get(`/registrations/event/${eventId}`);
    } catch (error) {
      console.error('Error fetching registrations by event:', error);
      throw error;
    }
  },
  
  getByEmail: async (email) => {
    try {
      return await api.get(`/registrations/search?email=${encodeURIComponent(email)}`);
    } catch (error) {
      console.error('Error searching registrations:', error);
      throw error;
    }
  },
  
  // New method for password-based booking access
  getMyBookings: async (email, password) => {
    try {
      console.log('🔐 Accessing my bookings with password');
      return await api.post('/registrations/my-bookings', { email, password });
    } catch (error) {
      console.error('Error accessing my bookings:', error);
      throw error;
    }
  },
  
  // PATCH method for check-in
  updateStatus: async (registrationId, status) => {
    try {
      console.log('📝 Updating registration status via PATCH:', registrationId, status);
      return await api.patch(`/registrations/${registrationId}/status`, { status });
    } catch (error) {
      console.error('Error updating registration status:', error);
      throw error;
    }
  },
};

/* =====================================================
   CHECK-INS API
===================================================== */
export const checkinsAPI = {
  verify: async (qrData) => {
    console.log('Verifying check-in:', qrData);
    try {
      return await api.post('/checkins/verify', { qrData });
    } catch (error) {
      console.error('Error verifying check-in:', error);
      throw error;
    }
  },
  
  getByEvent: async (eventId) => {
    if (!eventId || typeof eventId !== 'string') {
      throw new Error('Invalid event ID format');
    }
    try {
      return await api.get(`/checkins/event/${eventId}`);
    } catch (error) {
      console.error('Error fetching check-ins:', error);
      throw error;
    }
  },
};

/* =====================================================
   BOOKINGS API
===================================================== */
export const bookingsAPI = {
  verify: async (email, password) => {
    try {
      console.log('🔐 Verifying booking access');
      return await api.post('/bookings/verify', { email, password });
    } catch (error) {
      console.error('Error verifying booking access:', error);
      throw error;
    }
  },
  
  validate: async (eventId, ticketTier) => {
    try {
      return await api.post('/bookings', { eventId, ticketTier });
    } catch (error) {
      console.error('Error validating booking:', error);
      throw error;
    }
  },
};
export const adminAPI = {
  login: async (username, password) => {
    console.log('Admin login attempt');
    try {
      return await api.post('/admin/login', { username, password });
    } catch (error) {
      console.error('Error during login:', error);
      throw error;
    }
  },
  
  getDashboard: async (eventId) => {
    if (!eventId || typeof eventId !== 'string') {
      throw new Error('Invalid event ID format');
    }
    try {
      return await api.get(`/admin/dashboard/${eventId}`);
    } catch (error) {
      console.error('Error fetching dashboard:', error);
      throw error;
    }
  },
  
  exportRegistrations: async (eventId) => {
    if (!eventId || typeof eventId !== 'string') {
      throw new Error('Invalid event ID format');
    }
    try {
      return await api.get(`/admin/export/registrations/${eventId}`);
    } catch (error) {
      console.error('Error exporting registrations:', error);
      throw error;
    }
  },
  
  exportCheckins: async (eventId) => {
    if (!eventId || typeof eventId !== 'string') {
      throw new Error('Invalid event ID format');
    }
    try {
      return await api.get(`/admin/export/checkins/${eventId}`);
    } catch (error) {
      console.error('Error exporting check-ins:', error);
      throw error;
    }
  },
};

/* =====================================================
   HELPER FUNCTION: TEST API CONNECTION
===================================================== */
export const testApiConnection = async () => {
  try {
    console.log('🧪 Testing API connection to:', API_URL);
    const healthUrl = `${API_URL.replace('/api', '')}/api/health`;
    console.log('💊 Health check URL:', healthUrl);
    
    const response = await fetch(healthUrl, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
      mode: 'cors'
    });
    
    console.log('📊 Health check status:', response.status);
    
    const contentType = response.headers.get('content-type');
    if (!contentType || !contentType.includes('application/json')) {
      console.error('❌ Health check returned non-JSON response');
      const text = await response.text();
      console.error('Response:', text.slice(0, 500));
      return { 
        success: false, 
        error: 'Health endpoint returned non-JSON response. API may be misconfigured.',
        details: text.slice(0, 200)
      };
    }
    
    const data = await response.json();
    console.log('✅ API connection test successful:', data);
    return { success: true, data };
  } catch (error) {
    console.error('❌ API connection test failed:', error);
    return { success: false, error: error.message };
  }
};

/* =====================================================
   FEEDBACK API
===================================================== */
export const feedbackAPI = {
  sendThankYou: async (eventId, attendeeEmails, photoLink) => {
    try {
      return await api.post('/feedback/send-thankyou', { eventId, attendeeEmails, photoLink });
    } catch (error) {
      console.error('Error sending thank you emails:', error);
      throw error;
    }
  },
  
  sendFeedbackRequest: async (eventId, attendeeEmails, customMessage) => {
    try {
      return await api.post('/feedback/send-feedback-request', { eventId, attendeeEmails, customMessage });
    } catch (error) {
      console.error('Error sending feedback requests:', error);
      throw error;
    }
  },
  
  submitFeedback: async (data) => {
    try {
      return await api.post('/feedback/submit', data);
    } catch (error) {
      console.error('Error submitting feedback:', error);
      throw error;
    }
  },
  
  getByEvent: async (eventId) => {
    try {
      return await api.get(`/feedback/event/${eventId}`);
    } catch (error) {
      console.error('Error fetching feedback:', error);
      throw error;
    }
  },
  
  getStats: async (eventId) => {
    try {
      return await api.get(`/feedback/stats/${eventId}`);
    } catch (error) {
      console.error('Error fetching feedback stats:', error);
      throw error;
    }
  },
};

export default api;